"use client"

import { Bell, Send, AlertTriangle, Info, Check, History } from "lucide-react"
import { useNavigate } from "react-router-dom"

import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
  useNotificacionesDropdown,
  useMarcarComoLeida,
  useMarcarTodasLeidas,
} from '@/modules/notificaciones/hooks/useNotificaciones'
import type { TipoNotificacion } from '@/modules/notificaciones/types'

function getIconForType(tipo: TipoNotificacion) {
  switch (tipo) {
    case 'campania_enviada':
      return { icon: Send, bgClass: 'bg-green-100 text-green-600' }
    case 'campania_vencida':
      return { icon: AlertTriangle, bgClass: 'bg-yellow-100 text-yellow-600' }
    case 'estado_cambiado':
      return { icon: Info, bgClass: 'bg-blue-100 text-blue-600' }
    default:
      return { icon: Bell, bgClass: 'bg-gray-100 text-gray-600' }
  }
}

export function NotificationsDropdown() {
  const navigate = useNavigate()
  const { data, isLoading } = useNotificacionesDropdown()
  const marcarLeida = useMarcarComoLeida()
  const marcarTodas = useMarcarTodasLeidas()

  const contador = data?.contador ?? 0
  const notificaciones = data?.notificaciones ?? []
  const total = data?.total ?? 0

  const handleNotificacionClick = (notificacionId: number, url: string) => {
    marcarLeida.mutate(notificacionId)
    // Convertir URL legacy (/multichannel/campania/123/) a React (/multichannel/campania/123)
    const match = url.match(/\/multichannel\/campania\/(\d+)\//)
    if (match) {
      navigate(`/multichannel/campania/${match[1]}`)
    }
  }

  const handleMarcarTodas = () => {
    marcarTodas.mutate()
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9">
          <Bell className="h-6 w-6" />
          {contador > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-medium text-white">
              {contador > 9 ? '9+' : contador}
            </span>
          )}
          <span className="sr-only">Notificaciones</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notificaciones de Campañas</span>
          {contador > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
              onClick={handleMarcarTodas}
              disabled={marcarTodas.isPending}
            >
              <Check className="mr-1 h-3 w-3" />
              Marcar todas leídas
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Cargando...
            </div>
          ) : notificaciones.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No hay notificaciones nuevas
            </div>
          ) : (
            notificaciones.map((notificacion) => {
              const { icon: Icon, bgClass } = getIconForType(notificacion.tipo)
              return (
                <DropdownMenuItem
                  key={notificacion.id}
                  className="flex items-start gap-3 p-3 cursor-pointer"
                  onClick={() => handleNotificacionClick(notificacion.id, notificacion.url)}
                >
                  <div className={`mt-0.5 rounded-full p-2 ${bgClass}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">
                        {notificacion.campania_marca_secundaria || notificacion.campania_marca}
                      </p>
                      <span className="h-2 w-2 rounded-full bg-red-500" />
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {notificacion.campania_nombre}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {notificacion.campania_fecha}
                      {notificacion.campania_hora ? ` ${notificacion.campania_hora}` : ''}
                    </p>
                  </div>
                </DropdownMenuItem>
              )
            })
          )}
        </div>
        {total > 5 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center text-sm text-muted-foreground cursor-pointer"
              onClick={() => navigate('/notificaciones')}
            >
              <History className="mr-1 h-4 w-4" />
              Ver todas las notificaciones ({total} total)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
